import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useUsers, useCreateUser, useToggleUserStatus, useResetUserPassword } from "./queries";
import { UserEditDialog } from "./UserEditDialog";
import { ConfirmDialog } from "@shared/ui/ConfirmDialog";
import { useNotice } from "@shared/ui/NoticeDialog";
import { HttpError } from "@shared/http/client";
import type { SessionUser, Role } from "@/types/session";

const ROLES: Role[] = ["Administrador", "Evaluador", "Programador", "Auditoria"];

const STATUSES = ["Activo", "Inactivo"] as const;
type Status = typeof STATUSES[number];

const createSchema = z.object({
  name: z.string().min(1, "Nombre requerido").max(160),
  email: z.string().email("Correo inválido").max(160),
  password: z.string().min(4, "Mínimo 4 caracteres").max(200),
  role: z.enum(ROLES as [Role, ...Role[]]),
  status: z.enum(STATUSES as unknown as [Status, ...Status[]]),
});
type CreateForm = z.infer<typeof createSchema>;

export function UsuariosPage() {
  const { data, isLoading, error } = useUsers();
  const createMut = useCreateUser();
  const toggleMut = useToggleUserStatus();
  const resetMut = useResetUserPassword();

  const { showError } = useNotice();
  const [editing, setEditing] = useState<SessionUser | null>(null);
  const [resetResult, setResetResult] = useState<{ username: string; password: string } | null>(null);
  const [confirmReset, setConfirmReset] = useState<SessionUser | null>(null);
  const [createServerError, setCreateServerError] = useState<string | null>(null);

  const { register, handleSubmit, reset, formState: { errors, isSubmitting } } = useForm<CreateForm>({
    resolver: zodResolver(createSchema),
    defaultValues: { name: "", email: "", password: "", role: "Evaluador", status: "Activo" },
  });

  const users = data ?? [];

  const onCreate = async (values: CreateForm) => {
    setCreateServerError(null);
    try {
      await createMut.mutateAsync(values);
      reset();
    } catch (e) {
      setCreateServerError(e instanceof HttpError ? e.message : "Error creando usuario.");
    }
  };

  const doReset = async (u: SessionUser) => {
    try {
      const r = await resetMut.mutateAsync(u.id);
      setResetResult({ username: u.username, password: r.tempPassword });
    } catch (e) {
      showError("No se pudo restablecer la contraseña", e instanceof HttpError ? e.message : "Error inesperado al restablecer la contraseña.");
    }
  };

  return (
    <div className="p-24">
      <div className="page-header">
        <h1>Gestión de usuarios</h1>
        <p>Crear, editar y restablecer contraseñas de las cuentas del sistema.</p>
      </div>

      {error instanceof HttpError && (
        <div className="alert-inline alert-inline--danger mt-16">Error cargando usuarios: {error.message}</div>
      )}

      <div className="card mb-16">
        <div className="card-header">
          <div className="card-title">➕ Crear nuevo usuario</div>
        </div>
        <form onSubmit={handleSubmit(onCreate)}>
          <div className="wizard-grid">
            <label className="form-field">
              <span>Nombre completo</span>
              <input {...register("name")} className={errors.name ? "campo-requerido-error" : ""} />
              {errors.name && <div className="alert-inline alert-inline--danger">{errors.name.message}</div>}
            </label>
            <label className="form-field">
              <span>Correo electrónico</span>
              <input type="email" {...register("email")} className={errors.email ? "campo-requerido-error" : ""} />
              {errors.email && <div className="alert-inline alert-inline--danger">{errors.email.message}</div>}
            </label>
            <label className="form-field">
              <span>Contraseña inicial</span>
              <input type="password" {...register("password")} className={errors.password ? "campo-requerido-error" : ""} />
              {errors.password && <div className="alert-inline alert-inline--danger">{errors.password.message}</div>}
            </label>
            <label className="form-field">
              <span>Rol</span>
              <select {...register("role")}>
                {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </label>
            <label className="form-field">
              <span>Estado</span>
              <select {...register("status")}>
                {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </label>
          </div>
          {createServerError && (
            <div className="alert-inline alert-inline--danger alert-inline--md mt-8">⚠️ {createServerError}</div>
          )}
          <div className="mt-16">
            <button type="submit" className="btn btn-primary" disabled={isSubmitting || createMut.isPending}>
              {createMut.isPending ? "Creando…" : "Crear usuario"}
            </button>
          </div>
        </form>
      </div>

      {isLoading && <div className="text-muted">Cargando usuarios…</div>}

      <div className="card">
        <div className="card-header">
          <div className="card-title">👥 Usuarios registrados {users.length > 0 && <span className="badge badge-blue ml-8">{users.length}</span>}</div>
        </div>
      {users.length > 0 && (
        <div className="table-scroll">
          <table className="data-table">
            <thead>
              <tr>
                <th>Nombre</th>
                <th>Usuario / Correo</th>
                <th>Rol</th>
                <th>Estado</th>
                <th>Clave temp</th>
                <th style={{ textAlign: "right" }}>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id}>
                  <td>{u.name}</td>
                  <td>{u.email || u.username}</td>
                  <td>{u.role}</td>
                  <td>
                    <span className={`badge ${u.status === "Activo" ? "badge-blue" : "badge-gray"}`}>{u.status}</span>
                  </td>
                  <td>{u.tempPassword === "Si" ? "⚠️ Sí" : "—"}</td>
                  <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                    <button type="button" className="btn btn-sm btn-secondary" onClick={() => setEditing(u)}>
                      Editar
                    </button>
                    <button
                      type="button"
                      className="btn btn-sm btn-secondary ml-8"
                      onClick={() => toggleMut.mutate(u.id)}
                      disabled={toggleMut.isPending}
                    >
                      {u.status === "Activo" ? "Inactivar" : "Activar"}
                    </button>
                    <button
                      type="button"
                      className="btn btn-sm btn-danger ml-8"
                      onClick={() => setConfirmReset(u)}
                      disabled={resetMut.isPending}
                    >
                      Reset clave
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      </div>

      <UserEditDialog user={editing} onClose={() => setEditing(null)} />

      <ConfirmDialog
        open={!!confirmReset}
        title="Restablecer contraseña"
        message={
          confirmReset
            ? `Se generará una contraseña temporal para ${confirmReset.name}. Solo se mostrará una vez y el usuario deberá cambiarla en su próximo login.`
            : ""
        }
        confirmLabel={resetMut.isPending ? "Generando…" : "Sí, restablecer"}
        cancelLabel="Cancelar"
        tone="danger"
        onCancel={() => setConfirmReset(null)}
        onConfirm={() => {
          if (!confirmReset) return;
          const u = confirmReset;
          setConfirmReset(null);
          void doReset(u);
        }}
      />

      {resetResult && (
        <div className="reset-result-modal" role="alertdialog">
          <div className="reset-result-card">
            <h4>Contraseña temporal generada</h4>
            <p>Para <strong>{resetResult.username}</strong>. Cópiala ahora — no se volverá a mostrar. El usuario deberá cambiarla en su próximo login.</p>
            <div className="reset-result-password">{resetResult.password}</div>
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 16 }}>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => { void navigator.clipboard.writeText(resetResult.password); }}
              >
                Copiar
              </button>
              <button type="button" className="btn btn-primary" onClick={() => setResetResult(null)}>
                Ya la guardé
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
